import React, { useEffect, useState, useRef } from "react"
import {
  Box,
  Button,
  CardMedia,
  Container,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"

import { useSnackbarContext } from "../services/snackbar"
import { useNavigate } from "react-router-dom"
import { DirectQueryType, DirectType } from "../services/api/direct"
import { FieldContainer, HoverOverlay, Overlay, PageTitle, StyledTextField } from "../components/StyledElements"
import { FormatDate } from "../components/Util"
import { useDirectAPI } from "../services/api/hooks/useDirectAPI"
import { httpClient } from "../services/http"
import { useStaticContext } from "../services/static"
import { VideoQueryType, VideoType } from "../services/api/video"
import AspectRatioBox from "../components/AspectRatioBox"
import { VideoThumbnailImage } from "../components/VideoThumbnail"
import { useVideoAPI } from "../services/api/hooks/useVideoAPI"
import { Motd } from "../components/Motd"

import { ReactComponent as LockIcon } from "../assets/svg/lock.svg"
import { ReactComponent as KeyIcon } from "../assets/svg/key.svg"
import { ReactComponent as InternetIcon } from "../assets/svg/internet.svg"
import useWindowDimensions from "../services/useWindowDimensions"

export const Settings = (): React.ReactElement => {
  const [directSearch, setDirectSearch] = useState<string>("")
  const [direct, setDirect] = useState<DirectType | null>(null)
  const [gpaPromo, setGpaPromo] = useState<number | null>(null)
  const [homeDisplay, setHomeDisplay] = useState<number>(0)
  const [trendingVideos, setTrendingVideos] = useState<VideoType[]>([])
  const [posterSource, setPosterSource] = useState<string | null>(null)
  const [motd, setMotd] = useState<string>("")
  const [motdPrivacy, setMotdPrivacy] = useState<number>(1)
  const [prevMotdPrivacy, setPrevMotdPrivacy] = useState<number>(1)

  const theme = useTheme()
  const { open: sbOpen } = useSnackbarContext()
  const {
    homeDisplay: prevHomeDisplay,
    currentDirect: prevDirect,
    currentDirectPosterSource,
    trendingVideos: prevTrendingVideos,
    motd: prevMotd,
    loading,
    reload,
  } = useStaticContext()
  const navigate = useNavigate()

  const { directsData, loading: directsLoading } = useDirectAPI({
    constraint: DirectQueryType.SEARCH,
    search: directSearch,
  })

  const handleDirectFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDirect(null)
    setDirectSearch(e.target.value)
  }

  const handleHomeDisplayChange = (e: SelectChangeEvent<number>) => {
    setHomeDisplay(e.target.value as number)
  }

  const handleTrendingVideoChange = (index: number) => (video: VideoType) => {
    setTrendingVideos((prevVideos) => {
      const newVideos = [...prevVideos]
      newVideos[index] = video
      return newVideos
    })
  }

  const handleGpaPromoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (gpaPromo === null) return
    if (isNaN(Number(e.target.value))) return
    setGpaPromo(Number(e.target.value))
  }

  const handleMotdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMotd(e.target.value)
  }

  const handleMotdPrivacyChange = (e: SelectChangeEvent<number>) => {
    setMotdPrivacy(e.target.value as number)
  }

  const handleSubmit = async () => {
    if (loading) return
    try {
      if (direct !== prevDirect) await httpClient.instance.put(`currentDirect/${direct ? direct.id : -1}`)
      if (homeDisplay !== prevHomeDisplay) await httpClient.instance.put(`homeDisplay/${homeDisplay}`)
      if (gpaPromo !== null) await httpClient.instance.put(`gpaPromo/${gpaPromo}`)
      if (motd !== prevMotd || motdPrivacy !== prevMotdPrivacy)
        await httpClient.instance.put(
          `motd`,
          { motd: motd, privacy: motdPrivacy },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )
      for (let score = 1; score <= 5; score++) {
        const videoId = trendingVideos[5 - score] ? trendingVideos[5 - score].id : -1
        await httpClient.instance.put(`videos/trending/${score}/${videoId}`)
      }
      sbOpen("Modifications enregistrées", "success")
    } catch (error) {
      sbOpen("Une erreur s'est produite", "error")
      console.error(error)
    } finally {
      reload()
      navigate(`/`)
    }
  }

  useEffect(() => {
    setHomeDisplay(prevHomeDisplay)
    setDirect(prevDirect)
    setTrendingVideos(prevTrendingVideos)
    setPosterSource(currentDirectPosterSource)
    setMotd(prevMotd || "")
  }, [prevTrendingVideos, prevDirect, prevHomeDisplay, prevMotd])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { gpaPromo: promo } = await httpClient.instance.get<{
          gpaPromo: number
        }>(`gpaPromo`)
        setGpaPromo(promo)
        const { privacy } = await httpClient.instance.get<{
          privacy: number
        }>(`motd/privacy`)
        setMotdPrivacy(privacy)
        setPrevMotdPrivacy(privacy)
      } catch (error) {
        sbOpen("Une erreur s'est produite", "error")
        navigate(`/`)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    const fetchPoster = async () => {
      if (direct) {
        try {
          const response = await fetch(`/api/media/poster/${direct.id}`, {
            credentials: "same-origin",
          })

          if (response.status === 200) {
            const blob = await response.blob()

            const posterURL = URL.createObjectURL(blob)
            setPosterSource(posterURL)
          } else {
            setPosterSource(null)
          }
        } catch (error) {
          console.error("Error fetching poster :", error)
          setPosterSource(null)
        }
      } else {
        setPosterSource(null)
      }
    }
    fetchPoster()
  }, [direct])

  return (
    <Box
      sx={{
        position: theme.name === "glass" ? "static" : "relative",
        overflow: theme.name === "glass" ? "visible" : "hidden",
      }}
    >
      {homeDisplay && direct && posterSource ? (
        <Box
          sx={{
            position: "absolute",
            top: "0",
            overflow: theme.name === "glass" ? "visible" : "hidden",
            width: "100%",
            minHeight: "100vh",
            maxHeight: "200vh",
          }}
        >
          <CardMedia
            component="img"
            image={posterSource}
            sx={{
              userSelect: "none",
              left: "50%",
              width: "100%",
              objectFit: "cover",
              zIndex: 100,
              transform: `translateY(min(${theme.name === "glass" ? -81 : 0}px,calc(300px - 30vw)))`,
              opacity: 0.4,
              filter: "blur(.5vw)",
              maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 20%, rgba(0,0,0,0) min(100%, 200vh))",
            }}
          />
        </Box>
      ) : (
        <></>
      )}
      <Box sx={{ position: "relative", zIndex: 200 }}>
        {motd !== null && motd !== "" && <Motd motd={motd} />}
        <PageTitle>{"Paramètres généraux"}</PageTitle>
        <Container maxWidth="lg">
          <Grid container direction="row">
            <FieldContainer size={direct ? 10 : 12}>
              <StyledTextField
                label="DT à la une"
                variant="standard"
                color="soft"
                value={direct ? direct.title : directSearch}
                onChange={handleDirectFieldChange}
                focused
              />
              {!directsLoading && !direct && directsData.length > 0 && (
                <Box sx={{ position: "absolute", zIndex: 10, minWidth: "30%", maxWidth: "100%" }}>
                  {directsData.slice(0, 3).map((direct) => (
                    <Box
                      key={direct.id}
                      sx={{
                        backgroundColor: "primary.main",
                        "&:hover": { backgroundColor: "primary.light" },
                        transition: ".2s",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setDirect(direct)
                        if (homeDisplay === 0) setHomeDisplay(1)
                      }}
                    >
                      <Typography sx={{ padding: 1, pl: 2 }} textAlign={"start"} textOverflow={"ellipsis"} noWrap>
                        {direct.title}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </FieldContainer>
            {direct && (
              <FieldContainer size={2}>
                <Typography color="darkgrey" align="center">
                  {direct.id}
                </Typography>
              </FieldContainer>
            )}
            <FieldContainer size={12}>
              <Select
                value={homeDisplay}
                variant="standard"
                color="soft"
                onChange={handleHomeDisplayChange}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value={0}>{"Classique"}</MenuItem>
                <MenuItem value={1}>{"Thème"}</MenuItem>
                <MenuItem value={2}>{"Prochain DT"}</MenuItem>
                <MenuItem value={3}>{"DT en cours"}</MenuItem>
              </Select>
              <FormHelperText>{"Page d'accueil"}</FormHelperText>
            </FieldContainer>
            <FieldContainer size={12}>
              <Typography component="h3" variant="h3" fontSize={"2rem"} mb={2}>
                {"Vidéos à la une"}
              </Typography>
              <Grid container spacing={3} direction="row">
                {trendingVideos[0] && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <VideoSelector video={trendingVideos[0]} setVideo={handleTrendingVideoChange(0)} />
                  </Grid>
                )}
                <Grid container spacing={3} size={{ xs: 12, md: 6 }} direction="row">
                  {trendingVideos[1] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoSelector video={trendingVideos[1]} setVideo={handleTrendingVideoChange(1)} />
                    </Grid>
                  )}
                  {trendingVideos[2] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoSelector video={trendingVideos[2]} setVideo={handleTrendingVideoChange(2)} />
                    </Grid>
                  )}
                  {trendingVideos[3] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoSelector video={trendingVideos[3]} setVideo={handleTrendingVideoChange(3)} />
                    </Grid>
                  )}
                  {trendingVideos[4] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoSelector video={trendingVideos[4]} setVideo={handleTrendingVideoChange(4)} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </FieldContainer>
            <FieldContainer size={{ xs: 12, md: 8 }}>
              <StyledTextField
                label="Message du jour"
                variant="standard"
                color="soft"
                value={motd}
                onChange={handleMotdChange}
                focused
              />
            </FieldContainer>
            <FieldContainer size={{ xs: 12, md: 4 }}>
              <Select
                value={motdPrivacy}
                variant="standard"
                color="soft"
                onChange={handleMotdPrivacyChange}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value={1}>{"CS"}</MenuItem>
                <MenuItem value={2}>{"Hyris"}</MenuItem>
              </Select>
              <FormHelperText>{"Visibilité"}</FormHelperText>
            </FieldContainer>
            <FieldContainer size={{ xs: 12, md: 4 }}>
              <StyledTextField
                label="Promo des GPA"
                variant="standard"
                color="soft"
                value={gpaPromo !== null ? gpaPromo : ""}
                onChange={handleGpaPromoChange}
                focused
              />
            </FieldContainer>
            <FieldContainer size={12}>
              <Button
                variant="contained"
                color="secondary"
                disabled={loading}
                onClick={handleSubmit}
                sx={{ margin: 1 }}
              >
                {"Valider"}
              </Button>
            </FieldContainer>
          </Grid>
        </Container>
      </Box>
    </Box>
  )
}

const VideoSelector = ({
  video,
  setVideo,
}: {
  video: VideoType
  setVideo: (video: VideoType) => void
}): React.ReactElement => {
  const theme = useTheme()
  const [displaySearch, setDisplaySearch] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const { videosData, loading } = useVideoAPI({
    constraint: VideoQueryType.SEARCH,
    search: searchTerm,
  })
  const { width } = useWindowDimensions()

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setDisplaySearch(false)
        setSearchTerm("")
      }
    }
    document.addEventListener("click", handleDocumentClick)
    return () => document.removeEventListener("click", handleDocumentClick)
  }, [])

  return (
    <Box mb={2} ref={wrapperRef} onClick={() => setDisplaySearch(true)}>
      <HoverOverlay
        sx={{ display: "flex", flexDirection: "column", height: "fit-content", cursor: "pointer", overflow: "visible" }}
      >
        <AspectRatioBox ratio={16 / 9}>
          <VideoThumbnailImage video={video} adaptiveBorder={false} />
          <Overlay className={displaySearch ? "overlay obscured" : "overlay"} borderRadius={"10px"} />
          {displaySearch && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "start",
                alignItems: "center",
                padding: 2,
                paddingTop: "10%",
                zIndex: 3,
                overflow: "visible",
              }}
            >
              <TextField
                variant="outlined"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                sx={{ width: "100%", backgroundColor: "primary.dark", backgroundOpacity: 0.5 }}
              />
              {!loading && videosData.length > 0 && (
                <Box sx={{ minWidth: "100%" }}>
                  {videosData.slice(0, 4).map((video) => (
                    <Box
                      sx={{
                        backgroundColor: "primary.main",
                        "&:hover": { backgroundColor: "primary.light" },
                        transition: ".2s",
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setVideo(video)
                        setDisplaySearch(false)
                        setSearchTerm("")
                      }}
                    >
                      <Typography sx={{ padding: 1, pl: 2 }} textAlign="start" textOverflow="ellipsis" noWrap>
                        {video.title}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </AspectRatioBox>
        <Box mt={1} sx={{ position: "relative", zIndex: 2 }}>
          <Box display="flex" alignItems="center" minWidth={0}>
            <Typography noWrap variant="h5" fontSize={"1.2rem"} textOverflow="ellipsis" sx={{ minWidth: 0 }}>
              {video.title}
            </Typography>
            {video.privacy !== 1 && (
              <Box ml={"4px"} component={"span"} display="inline-flex" alignItems="center" flexShrink={0}>
                {video.privacy === 0 ? (
                  <Tooltip title={"Vidéo publique"}>
                    <InternetIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                  </Tooltip>
                ) : video.privacy === 2 ? (
                  <Tooltip title={video.gpaRestricted ? "Vidéo caché aux GPA" : "Vidéo privée"}>
                    <KeyIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                  </Tooltip>
                ) : (
                  video.privacy === 3 && (
                    <Tooltip title={"Vidéo archivée"}>
                      <LockIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                    </Tooltip>
                  )
                )}
              </Box>
            )}
          </Box>
          <Typography noWrap variant="subtitle1" fontSize={".8rem"} color={"gray"} textOverflow="ellipsis">
            {FormatDate(video.date, width < theme.breakpoints.values.lg) +
              " - " +
              video.views +
              " vue" +
              (video.views > 1 ? "s" : "") +
              (video.likeCount ? " - " + video.likeCount + " like" + (video.likeCount > 1 ? "s" : "") : "")}
          </Typography>
        </Box>
      </HoverOverlay>
    </Box>
  )
}
